import { html, LitElement, TemplateResult, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { CategoriesList, INCIDENT_TYPE, IncidentEvents } from '../../../incidentsGrid/types';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { EventsName } from '../../../../xdr/organizationalImpact/types/types';
import { IncidentType, XdrIncidentTabPanels } from '../../../incidentsSideMenu/types';
import { ProcessedData } from '../index';
import { processEntitiesRowData, processResourcesRowData } from '../../../../utils/xdr.utils';
import { generateIncidentLink } from '../../../incidentsGrid/view/CustomGridRenderers';
import { translateFn } from '../../../../utils/translator.helper';

/**
 * @summary Type here the description of this component
 * @tag incident-attack-info
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('incident-attack-info')
export class IncidentAttackInfoWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({ attribute: false })
  translations;

  @property({
    type: String,
    attribute: false,
  })
  actionTaken;

  @property({
    type: Number,
    attribute: false,
  })
  alerts;

  @property({
    type: Array,
    attribute: false,
  })
  entities;

  @property({
    type: Array,
    attribute: false,
  })
  resources;

  @property({
    type: Array,
    attribute: false,
  })
  correlatedIncidents;

  @property({
    type: Array,
    attribute: false,
  })
  killChainPhases;

  @property({
    type: Object,
    attribute: false,
  })
  incidentData;

  @property({ attribute: false })
  operatingSystem;

  @property({ attribute: false })
  artifacts;

  processedData: ProcessedData = {
    resources: undefined,
    entities: undefined,
  };

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div>
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title mb-2 text-uppercase"
          >${this.translations.sectionTitle}
        </gz-title>
        ${this.renderActionTaken()} ${this.renderAlerts()} ${this.renderResources()} ${this.renderEntities()}
        ${this.renderOperatingSystem()} ${this.renderArtifacts()} ${this.renderCorrelatedIncidents()}
        ${this.renderKillChainPhases()}${this.renderActors()}
      </div>
    `;
  }

  constructor() {
    super();
  }

  protected shouldUpdate(_changedProperties): boolean {
    return super.shouldUpdate(_changedProperties);
  }

  renderAlerts() {
    if (this.alerts) {
      return html`
        <gz-labeled-content label="${this.translations.alerts}">
          <gz-link @click="${e => this.handleIncidentAlertsClicked(e, this.incidentData)}">${this.alerts}</gz-link>
        </gz-labeled-content>
      `;
    } else {
      return nothing;
    }
  }

  renderResources() {
    if (this.resources && this.resources.length) {
      this.processedData.resources = this.processedResources(this.resources);
      const templateItems = this.buildResourcesItems(this.processedData.resources);
      return html` <show-more-items
        id="resources"
        .items=${templateItems}
        .showMoreButtonText="${this.translations.showMoreBtn}"
        .title=${this.translations.resources}
      >
      </show-more-items>`;
    } else {
      return nothing;
    }
  }

  renderActionTaken() {
    if (this.actionTaken) {
      return html` <gz-labeled-content label="${this.translations.actionTaken}">
        ${this.actionTaken}
      </gz-labeled-content>`;
    }
  }

  renderEntities() {
    if (this.entities && this.entities.length > 0) {
      this.processedData.entities = this.getProcessedEntities(
        this.entities,
        this.correlatedIncidents,
        this.incidentData
      );
      const templateItems = this.buildEntitiesItems(this.processedData.entities);
      return html` <show-more-items
        id="entities"
        .items=${templateItems}
        .showMoreButtonText="${this.translations.showMoreBtn}"
        .title=${this.translations.entities}
      >
      </show-more-items>`;
    } else {
      return nothing;
    }
  }

  renderOperatingSystem() {
    if (this.operatingSystem) {
      return html` <gz-labeled-content label="${this.translations.operatingSystem}">
        ${Translator.translateOperatingSystem(this.operatingSystem)}
      </gz-labeled-content>`;
    }

    return nothing;
  }

  renderArtifacts() {
    if (this.artifacts) {
      return html` <gz-labeled-content label="${this.translations.artifacts}"> ${this.artifacts}</gz-labeled-content>`;
    }

    return nothing;
  }

  renderCorrelatedIncidents() {
    if (this.correlatedIncidents && this.correlatedIncidents.length) {
      const templateItems = this.buildCorrelatedIncidentsItems(this.correlatedIncidents);
      return html` <show-more-items
        id="correlatedIncidents"
        .items=${templateItems}
        .showMoreButtonText="${this.translations.showMoreBtn}"
        .title=${this.translations.correlatedIncidents}
      >
      </show-more-items>`;
    } else {
      return nothing;
    }
  }

  handleIncidentAlertsClicked(e, incident) {
    let extraDetails: any = {
      tab: XdrIncidentTabPanels.ALERTS,
    };

    if (incident.type === INCIDENT_TYPE.EDR) {
      extraDetails = {
        showEvents: true,
        showAlertsTabFromEvents: true,
      };
    }

    const event = new CustomEvent(IncidentEvents.SHOW_INCIDENT, {
      detail: {
        incident: incident,
        extraDetails,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  buildResourcesItems(resources: Array<CategoriesList>) {
    const templateItems: Array<TemplateResult> = [];
    resources.forEach(resource => {
      templateItems.push(html`
        <div class="resource-container d-flex flex-row">
          <gz-icon-label
            icon="${resource.icon}"
            iconColor="var(--chart-302-icon-buttons-titles)"
            @click=${e => this.handleResourceIconClicked(e, resource)}
          >
          </gz-icon-label>
          <gz-tooltip isEllipsis tooltipText="${resource.items.length} ${resource.text}">
            <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
            >${resource.items.length} ${resource.text}
          </gz-text>
        </gz-tooltip>
      </div>`);
    });
    return templateItems;
  }

  handleResourceIconClicked(e, resource: CategoriesList) {
    const clickEvent = new CustomEvent(EventsName.RESOURCES_CLICK, {
      detail: {
        type: resource.type,
        resources: this.processedData.resources,
        incidentId: this.incidentData._id,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(clickEvent);
  }

  processedResources(resources): Array<CategoriesList> {
    return processResourcesRowData({ resources, ...this.incidentData }, this.setResourcesStringTranslate);
  }

  getProcessedEntities(entities, correlatedIncidents, incidentData): Array<CategoriesList> {
    return processEntitiesRowData(
      {
        entities,
        correlatedIncidents,
        ...incidentData,
      },
      this.setEntityStringTranslate
    );
  }

  setEntityStringTranslate(type: number): string {
    return Translator.translateXdrEntityType(type);
  }

  setResourcesStringTranslate(type: number): string {
    return Translator.translateXdrResourceType(type);
  }

  translateKillChainPhases(type: number) {
    return Translator.translateKillChainPhases(type);
  }

  buildEntitiesItems(entities): Array<TemplateResult> {
    const templateItems: Array<TemplateResult> = [];

    entities.forEach(entity => {
      if (entity.items.length === 1) {
        templateItems.push(html`
          <div class="entities-container d-flex flex-row">
            <gz-icon-label
              @click=${e => this.handleEntityIconClicked(e, entity)}
              icon="${entity.icon}"
              iconColor="var(--chart-302-icon-buttons-titles)"
            ></gz-icon-label>
            <gz-tooltip isEllipsis tooltipText="${entity.items.length} ${entity.text}: ${entity.items[0].name}">
              <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
              >${entity.items.length} ${entity.text}: ${entity.items[0].name}
            </gz-text>
          </gz-tooltip>
        </div>`);
      } else {
        templateItems.push(html`
          <div class="entities-container d-flex" flex-row>
            <gz-icon-label
              icon="${entity.icon}"
              iconColor="var(--chart-302-icon-buttons-titles)"
              @click=${e => this.handleEntityIconClicked(e, entity)}
            ></gz-icon-label>
            <gz-tooltip isEllipsis tooltipText="${entity.items.length} ${entity.text}">
              <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
              >${entity.items.length} ${entity.text}
            </gz-text>
          </gz-tooltip>
        </div>`);
      }
    });
    return templateItems;
  }

  handleEntityIconClicked(e, entity: CategoriesList) {
    const clickEvent = new CustomEvent(EventsName.ENTITIES_CLICK, {
      detail: {
        type: entity.type,
        entities: this.processedData.entities,
        incidentId: this.incidentData._id,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(clickEvent);
  }

  buildCorrelatedIncidentsItems(correlatedIncidents) {
    const templateResult: Array<TemplateResult> = [];

    correlatedIncidents.forEach(correlatedIncident => {
      templateResult.push(html`
        <div class="container d-flex flex-row align-items-center" style="height: 30px;">
          <gz-link
            fontSize="${FontSizes.PARAGRAPH_12}"
            @click="${e => this.handleCorrelatedIncidentClicked(e, correlatedIncident)}"
            >#${this.incidentData.isGzDemo ? 'DEMO-' + correlatedIncident.demoIncidentOrder : correlatedIncident.number}
          </gz-link>
          ${this.renderEndpointForCorrelatedIncident(correlatedIncident)}
          <gz-copy-to-clipboard
            .confirmationMessage="${translateFn('Copied')}"
            .clipboardMessage="${generateIncidentLink(correlatedIncident.id, correlatedIncident.type)}"
            class="copy-content"
            style="width: 24px; margin-left: auto; margin-right: 16px; padding-top: 3px"
          >
          </gz-copy-to-clipboard>
        </div>
      `);
    });
    return templateResult;
  }

  renderEndpointForCorrelatedIncident(correlatedIncident) {
    if (correlatedIncident.node) {
      return html`
        <gz-tooltip
          isEllipsis
          tooltiptext="${this.getCorrelatedIncidentNodeTitle(correlatedIncident)}"
          style="padding-left: 4px;"
          >${this.getCorrelatedIncidentNodeTitle(correlatedIncident)}
        </gz-tooltip>
      `;
    }
  }

  getCorrelatedIncidentNodeTitle(correlatedIncident) {
    return this.translations.onEntityText + ' ' + correlatedIncident.node.name;
  }

  handleCorrelatedIncidentClicked(e, incident) {
    let isEdrIncident = true;
    const params: Object = {
      type: incident.type,
      _id: incident.id,
    };
    if (incident.type === IncidentType.XDR) {
      isEdrIncident = false;
    }
    Application.lib.edrUtils.openIncidentInNewTab(params, isEdrIncident);
  }

  renderKillChainPhases() {
    if (this.killChainPhases && this.killChainPhases.length) {
      const templateItems = this.buildKillChainPhasesItems(this.killChainPhases);

      return html` <show-more-items
        id="killChainPhases"
        .items=${templateItems}
        .showMoreButtonText="${this.translations.showMoreBtn}"
        .title=${this.translations.killChainPhases}
      >
      </show-more-items>`;
    } else {
      return nothing;
    }
  }

  renderActors() {
    if (this.incidentData?.specifics?.attribution?.actors?.length) {
      const formattedActors = this.incidentData.specifics.attribution.actors.map(actor => {
        return actor.name;
      });
      const actorsText = formattedActors.join(', ');
      return html` <gz-labeled-content label="${this.translations.actorsTitle}">
        <gz-tooltip tooltiptext="${actorsText}">
          <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}">${actorsText} </gz-text>
        </gz-tooltip>
      </gz-labeled-content>`;
    } else {
      return nothing;
    }
  }

  buildKillChainPhasesItems(killChainPhases) {
    const templateResult: Array<TemplateResult> = [];

    killChainPhases.forEach(phase => {
      templateResult.push(html` <gz-tooltip isEllipsis tooltiptext="${this.translateKillChainPhases(phase)}">
        <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}"
          >${this.translateKillChainPhases(phase)}
        </gz-text>
      </gz-tooltip>`);
    });
    return templateResult;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'incident-attack-info': IncidentAttackInfoWebComponent;
  }
}
